import React, { useState } from "react";
import { Grid2 as Grid, TextField, FormControlLabel, Checkbox, FormControl, FormLabel, RadioGroup, Radio, InputLabel, Select, Input, MenuItem, ListItemText, FormHelperText, Autocomplete, OutlinedInput, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { CustomIcon } from "./CustomIcon";
import { CustomDatePicker } from "@zimyo/components";

const FormBuilder = ({ fields = [], values = {}, inputHandler = () => { }, size = '', errors = {}, children, ...rest }) => {
    if (!size) {
        size = 'lg';
    }
    const [autoVal, setAutoVal] = useState({});

    const handleCheckboxChange = (e) => {
        if (e.target.checked) {
            e.target.value = 1;
        } else {
            e.target.value = 0;
        }
        inputHandler(e);
    }

    const validateInput = (e, type, old_val) => {
        let [real, ...fraction] = [];
        switch (type) {
            case 'A':
                e.target.value = e.target.value.replace(/[^A-Za-z]/g, '');
                break;
            case 'AS':
                e.target.value = e.target.value.replace(/[^A-Z a-z]/g, '');
                break;
            case 'AN':
                e.target.value = e.target.value.replace(/\W/g, '');
                break;
            case 'ASN':
                e.target.value = e.target.value.replace(/[^A-Z a-z0-9]/g, '');
                break;
            case 'ANS':
                e.target.value = e.target.value.replace(/[^A-Za-z 0-9_\-!@%&*?=+,.]/g, '');
                break;
            case 'N':
                e.target.value = e.target.value.replace(/\D/g, '');
                break;
            case 'F':
                [real, ...fraction] = e.target.value.replace(/[^0-9.]/g, '').split(".");
                e.target.value = fraction.length > 0 ? `${real}.${fraction.join('')}` : Number(real);
                break;
            case 'P':
                [real, ...fraction] = e.target.value.replace(/[^0-9.]/g, '').split(".");
                if (fraction.length > 0) {
                    let f = Number(fraction.join('')) || 0;
                    e.target.value = real > 100 || real < 0 || f > 99 || (f > 0 && real > 99) || fraction.join('').length > 2 ? old_val : `${Number(real)}.${fraction.join('')}`;
                } else {
                    e.target.value = real > 100 || real < 0 ? old_val : Number(real);
                }
                break;
            case 'MNO':
                e.target.value = e.target.value.replace(/\D/g, '').substring(0, 10);
                break;
            case 'PIN':
                e.target.value = e.target.value.replace(/\D/g, '').substring(0, 6);
                break;
            case 'EMAIL':
                e.target.value = e.target.value.replace(/[^A-Za-z0-9_\-@.]/g, '');
                break;
            case 'EXPIRY_DATE':
                e.target.value = e.target.value.replace(/[^0-9\/]/g, '')
                    .replace(/[^0-9\/]/g, '')                // Allow only numbers and /
                    .replace(/(\d{2})(\d{1,2})?$/, '$1/$2')  // Automatically add / after the first two digits (month)
                    .substring(0, 5);
                break;
            case 'CVV':
                e.target.value = e.target.value.replace(/[^0-9]/g, '').substring(0, 3);
                break;
            case 'CARD':
                e.target.value = e.target.value.replace(/[^0-9]/g, '').replace(/(\d{4})(?=\d)/g, '$1 ').substring(0, 19);;
                break;
            default:
                break;
        }
        return e.target.value !== old_val;
    }

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    const FormLabeler = (options) => {
        return <FormLabel component="legend">
            {options.label}
            {options.required ? <span aria-hidden="true" className="MuiFormLabel-asterisk MuiInputLabel-asterisk"> *</span> : ''}
        </FormLabel>
    }
    const InputLabeler = (options) => {
        return <InputLabel htmlFor={options.name}>
            {options.label}
            {options.required ? <span aria-hidden="true" className="MuiFormLabel-asterisk MuiInputLabel-asterisk"> * </span> : ''}
        </InputLabel>
    }

    if (!fields || !inputHandler || !values || typeof fields !== "object" || typeof inputHandler !== "function") {
        return (
            <ol>
                <li>
                    fields : required and should be type of Array object.
                </li>
                <li>
                    values : required and should be type of object.
                </li>
                <li>
                    inputHandler : required and should be type of function.
                </li>
            </ol>
        );
    } else {
        return (
            <Grid container alignItems="baseline" spacing={2} {...rest}>{
                Object.values(fields || []).map((options, i) => {
                    let materail_element = null;
                    let dt = new Date();
                    dt.setHours(0);
                    dt.setMinutes(0);
                    dt.setSeconds(0);
                    dt.setMilliseconds(0);
                    let size_chart = [12, 6, 4, 3];
                    if (['xs', 'sm'].indexOf(size) !== -1) {
                        size_chart = [12, 12, 12, 12];
                    } else if (size === 'md') {
                        size_chart = [12, 12, 6, 6];
                    }
                    if (Object.hasOwnProperty.call(options, 'size_chart')) {
                        size_chart = options.size_chart;
                    }
                    let sizeObj = options?.size || { xs: size_chart[0], sm: size_chart[1], md: size_chart[2], lg: size_chart[3] }
                    switch (options.type) {
                        case 'input':
                            materail_element = (
                                <FormControl fullWidth variant="outlined" size="small">
                                    {InputLabeler(options)}
                                    <OutlinedInput
                                        size="small"
                                        error={errors[options?.name]}
                                        placeholder={options?.placeholder}
                                        label={options?.label}
                                        onChange={(e) => {
                                            if (
                                                validateInput(
                                                    e,
                                                    options.validate || 'ANY',
                                                    values[options.name] === undefined ? '' : values[options.name]
                                                )
                                            ) {
                                                inputHandler(e);
                                            }
                                        }}
                                        required={options.required || false}
                                        disabled={options.readonly || options.disabled || false}
                                        id={options.name || 'inp_id_' + i}
                                        name={options.name}
                                        value={values[options.name] === undefined ? '' : values[options.name]}
                                        startAdornment={
                                            options.prefix ? (
                                                <InputAdornment position="start">
                                                    <CustomIcon iconName={options.prefix} />
                                                </InputAdornment>
                                            ) : null
                                        }
                                        endAdornment={
                                            options.suffix ? (
                                                <InputAdornment position="end">
                                                    <CustomIcon iconName={options.suffix} />
                                                </InputAdornment>
                                            ) : null
                                        }
                                    />
                                </FormControl>
                            )
                            break;
                        case 'password':
                            materail_element = (
                                <FormControl fullWidth variant="outlined" size="small">
                                    {InputLabeler(options)}
                                    <OutlinedInput
                                        id={options.name || ('inp_id_' + i)}
                                        label={`${options.label} ${options.required ? '*' : ''} `}
                                        placeholder={options?.placeholder}
                                        name={options.name}
                                        value={values[options.name] === undefined ? '' : values[options.name]}
                                        type={autoVal[options.name] ? 'text' : options.type}
                                        onChange={(e) => {
                                            if (validateInput(e, options.validate || 'ANY', values[options.name] === undefined ? '' : values[options.name])) {
                                                inputHandler(e);
                                            }
                                        }}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => { setAutoVal({ ...autoVal, [options.name]: !autoVal[options.name] }) }}
                                                    onMouseDown={(event) => { event.preventDefault(); }}
                                                    edge="end"
                                                >
                                                    {autoVal[options.name] ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>
                            )
                            break;
                        case 'hidden':
                            materail_element = (
                                <Input
                                    type={options.type}
                                    required={options.required || false}
                                    id={options.name || ('inp_id_' + i)}
                                    label={''}
                                    name={options.name}
                                    value={values[options.name] === undefined ? '' : values[options.name]}
                                />
                            )
                            break;
                        case 'checkbox':
                            materail_element = (
                                <FormControlLabel
                                    control={
                                        <Checkbox color="primary"
                                            name={options.name}
                                            checked={(values[options.name] === 1 || values[options.name] === "1" || (options.checkedVal !== undefined && values[options.name] === options.checkedVal)) ? true : false}
                                            disabled={options.readonly || options.disabled || false}
                                            onChange={handleCheckboxChange}
                                        />
                                    }
                                    labelPlacement="end"
                                    label={options.label}
                                />
                            )
                            break;
                        case 'groupselect':
                            materail_element = (<FormControl variant="outlined" size="small" fullWidth>
                                {InputLabeler(options)}
                                <Select native
                                    value={values[options.name] === undefined ? '' : values[options.name]}
                                    onChange={inputHandler}
                                    required={options.required || false}
                                    placeholder={options?.placeholder}
                                    disabled={options.readonly || options.disabled || false}
                                    label={`${options.label}${options.required ? ' * ' : ''}`}
                                    inputProps={{
                                        name: options.name,
                                        id: options.name
                                    }}
                                >
                                    <option aria-label="None" value="" />
                                    {React.Children.toArray(options?.options?.map((opg, i) => {
                                        return <optgroup label={opg.key}>
                                            {React.Children.toArray(opg?.value?.map((opt, i) => {
                                                if (typeof opt === "string") {
                                                    opt = { id: opt, name: opt };
                                                }
                                                return <option value={opt.id}>{opt.name}</option>
                                            }))}
                                        </optgroup>
                                    }))}
                                </Select>
                            </FormControl>)
                            break;
                        case 'select':
                            materail_element = (<FormControl variant="outlined" size="small" fullWidth error={errors[options?.name]}>
                                {InputLabeler(options)}
                                <Select
                                    id={options.name}
                                    value={values[options.name] || []}
                                    onChange={inputHandler}
                                    required={options.required || false}
                                    disabled={options.readonly || options.disabled || false}
                                    name={options.name}
                                    placeholder={options?.placeholder}
                                    input={<OutlinedInput label={`${options.label}${options.required ? ' * ' : ''}`} />}
                                    renderValue={(selected) => {
                                        let SelectedName = '';
                                        let id = options.key || 'id';
                                        let name = options.value || 'name';
                                        options.options?.forEach(opt => {
                                            if (typeof opt != "object") {
                                                opt = { [id]: opt, [name]: opt };
                                            }
                                            if (`${selected}` == `${opt[id]}`) {
                                                SelectedName = opt[name];
                                            }
                                        });
                                        return SelectedName;
                                    }}
                                    MenuProps={MenuProps}
                                >
                                    {React.Children.toArray(options.options?.map((opt) => {
                                        let id = options.key || 'id';
                                        let name = options.value || 'name';
                                        if (typeof opt != "object") {
                                            opt = { [id]: opt, [name]: opt };
                                        }
                                        return (<MenuItem disabled={!opt[id]} value={`${opt[id]}`} sx={{ whiteSpace: 'normal' }}>
                                            <ListItemText primary={opt[name]} />
                                        </MenuItem>)
                                    }))}
                                </Select>
                                {errors[options?.name]?.message && <FormHelperText>{errors[options?.name]?.message}</FormHelperText>}
                            </FormControl>)
                            break;
                        case 'multiselect':
                            materail_element = (<FormControl variant="outlined" size="small" fullWidth>
                                {InputLabeler(options)}
                                <Select
                                    id={options.name}
                                    multiple
                                    placeholder={options?.placeholder}
                                    value={values[options.name] || []}
                                    onChange={inputHandler}
                                    required={options.required || false}
                                    disabled={options.readonly || options.disabled || false}
                                    name={options.name}
                                    input={<OutlinedInput label={`${options.label}${options.required ? ' * ' : ''}`} />}
                                    renderValue={(selected) => {
                                        let SelectedName = [];
                                        options.options?.forEach(opt => {
                                            let id = options.key || 'id'
                                            let name = options.value || 'name'
                                            if (typeof opt != "object") {
                                                opt = { [id]: opt, [name]: opt };
                                            }
                                            if (selected.indexOf(opt[id]) !== -1) {
                                                SelectedName.push(opt[name]);
                                            }
                                        });


                                        return SelectedName.join(', ');
                                    }}
                                    MenuProps={MenuProps}
                                >
                                    {React.Children.toArray(options.options?.map((opt) => {
                                        let id = options.key || 'id'
                                        let name = options.value || 'name'
                                        if (typeof opt != "object") {
                                            opt = { [id]: opt, [name]: opt };
                                        }

                                        return (<MenuItem value={opt[id]}>
                                            <Checkbox color="primary" checked={(values[options.name] || []).indexOf(opt[id]) > -1} />
                                            <ListItemText primary={opt[name]} />
                                        </MenuItem>)
                                    }))}
                                </Select>
                            </FormControl>)
                            break;
                        case 'radio':
                            materail_element = (
                                <FormControl component="fieldset" sx={{ mb: -2 }} >
                                    {FormLabeler(options)}
                                    <RadioGroup
                                        row
                                        aria-label={options.label}
                                        name={options.name}
                                        sx={{ margin: 0 }}
                                        value={values[options.name] === undefined ? '' : values[options.name]}
                                        onChange={inputHandler}
                                        required={options.required || false}
                                        disabled={options.readonly || options.disabled || false}
                                    >
                                        {React.Children.toArray(options?.options?.map((rad, i) => {
                                            if (typeof rad === "string") {
                                                rad = { id: rad, name: rad };
                                            }
                                            return <FormControlLabel value={rad.id} control={<Radio size="small" color="primary" />} label={rad.name} />
                                        }))}
                                    </RadioGroup>
                                </FormControl>
                            )
                            break;
                        case 'date':
                            materail_element = (
                                <CustomDatePicker
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    error={errors[options?.name]}
                                    placeholder={options?.placeholder}
                                    helperText={errors[options?.name]?.message}
                                    onChange={(e) => {
                                        if (validateInput(e, options.validate || 'ANY', values[options.name] === undefined ? '' : values[options.name])) {
                                            inputHandler(e);
                                        }
                                    }}
                                    formats={options.formats || 'y-M-d h:m a'}
                                    required={options.required || false}
                                    // maxDate={options.maxDate}
                                    // minDate={options.minDate}
                                    disabled={options.readonly || options.disabled || false}
                                    id={options.name || ('inp_id_' + i)}
                                    label={options.label}
                                    name={options.name}
                                    type="date-time"
                                // value={values[options.name] === undefined ? '' : values[options.name]}
                                />
                            )
                            break;
                        case 'time':
                            break;
                        case 'datetime':
                            break;
                        case 'month':
                            break;
                        case 'year':
                            break;
                        case 'switch':
                            break;
                        case 'textarea':
                            materail_element = (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    placeholder={options?.placeholder}
                                    error={errors[options?.name]}
                                    helperText={errors[options?.name]?.message}
                                    onChange={(e) => {
                                        if (validateInput(e, options.validate || 'ANY', values[options.name] === undefined ? '' : values[options.name])) {
                                            inputHandler(e);
                                        }
                                    }}
                                    required={options.required || false}
                                    disabled={options.readonly || options.disabled || false}
                                    id={options.name || ('inp_id_' + i)}
                                    label={options.label}
                                    name={options.name}
                                    value={values[options.name] === undefined ? '' : values[options.name]}
                                    multiline
                                    minRows={options.minRows || 1}
                                    rowsMax={3}
                                />
                            )
                            break;
                        case 'captcha':
                            break;
                        case 'autocomplete':
                            materail_element = (<Autocomplete
                                options={options?.options}
                                onChange={(e, newValue) => {
                                    if (!newValue || typeof newValue === 'string') {
                                        newValue = { id: newValue, name: newValue };
                                    }
                                    setAutoVal({ ...autoVal, [options.name]: newValue.name });
                                    inputHandler({ target: { name: options.name, value: newValue.id } });
                                }}
                                disabled={options.readonly || options.disabled || false}
                                value={autoVal[options.name] || ''}
                                getOptionLabel={(option) => {
                                    return (typeof option === "string") ? option : option.name;
                                }}
                                renderInput={(params) => <TextField {...params} label={options.label}
                                    required={options.required || false} variant="outlined" size="small" fullWidth />}
                            />)
                            break;
                        case 'file':
                            materail_element = (
                                <fieldset style={{ marginTop: '-7px', borderRadius: '4px' }}><legend>{options.label}
                                    {options.required && <span aria-hidden="true" className="MuiFormLabel-asterisk MuiInputLabel-asterisk" > * </span>}
                                </legend>
                                </fieldset >
                            );
                            break;
                        default:
                            break;
                    }
                    return React.Children.toArray(options.type === 'hidden' ? null : <Grid size={sizeObj} >{materail_element}</Grid>);
                })
            }{children}</Grid >
        );
    }
};
export default FormBuilder;